import { FC, useEffect, useRef, useState } from "react";
import "./ChatHighlightForm.scss";
import ModalDefault from "../../../components/ModalDefault/ModalDefault";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { Col, FormGroup, Row } from "react-bootstrap";
import ErrorMessage from "../../../components/ErrorMessage/ErrorMessage";
import { useChatHighlights } from "../../../providers/ChatHighlights/ChatHighlightsProvider";

// Ícones do toggle "Tipo de Mídia" — cor fixa, viram pretos sólidos quando inativo via
// filter: brightness(0) (ver ChatHighlightForm.scss).
const CloudUploadIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 32 32" aria-hidden="true">
    <path
      d="M8.36666 26.6666C6.41333 26.6666 4.74666 25.9799 3.37999 24.6133C2.01333 23.2466 1.32666 21.5866 1.32666 19.6266C1.32666 17.8933 1.87999 16.3733 2.99333 15.0599C4.10666 13.7466 5.51333 12.9599 7.22666 12.6933C7.67333 10.5399 8.71333 8.77327 10.36 7.4066C12.0067 6.03994 13.9067 5.35327 16.06 5.35327C18.5467 5.35327 20.6467 6.25994 22.36 8.07327C24.0733 9.8866 24.9267 12.0599 24.9267 14.5933V15.3933C26.5267 15.3466 27.88 15.8666 28.9933 16.9466C30.1067 18.0266 30.66 19.3866 30.66 21.0333C30.66 22.5666 30.1067 23.8866 28.9933 24.9999C27.88 26.1133 26.56 26.6666 25.0267 26.6666H16.9933V16.0666L19.06 18.1333C19.26 18.3333 19.4933 18.4333 19.76 18.4333C20.0267 18.4333 20.26 18.3333 20.46 18.1333C20.66 17.9333 20.76 17.6933 20.76 17.4133C20.76 17.1333 20.66 16.8933 20.46 16.6933L16.6933 12.8933C16.4933 12.6933 16.26 12.5933 15.9933 12.5933C15.7267 12.5933 15.4933 12.6933 15.2933 12.8933L11.4933 16.6933C11.2933 16.8933 11.2 17.1266 11.2067 17.3933C11.2133 17.6599 11.3267 17.8933 11.5267 18.0933C11.7267 18.2933 11.9667 18.3933 12.2467 18.3933C12.5267 18.3933 12.7667 18.2933 12.9667 18.0933L15 16.0599V26.6599H8.36666V26.6666Z"
      fill="currentColor"
    />
  </svg>
);

const LoomIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M18 7.99911H12.7365L17.2949 5.36732L16.2939 3.63294L11.7355 6.26473L14.3667 1.70668L12.6323 0.705033L10.0011 5.26307V0H7.99888V5.26357L5.36667 0.705033L3.63284 1.70618L6.26455 6.26423L1.70613 3.63294L0.705014 5.36681L5.26343 7.99861H0V10.0009H5.26293L0.705014 12.6327L1.70613 14.3671L6.26404 11.7358L3.63233 16.2938L5.36667 17.2949L7.99838 12.7364V18H10.0006V12.7369L12.6318 17.2949L14.3661 16.2938L11.7344 11.7353L16.2929 14.3671L17.294 12.6327L12.7361 10.0014H17.999V7.99911H18ZM9 11.7231C7.49027 11.7231 6.26656 10.4995 6.26656 8.98968C6.26656 7.4799 7.49027 6.25617 9 6.25617C10.5097 6.25617 11.7334 7.4799 11.7334 8.98968C11.7334 10.4995 10.5097 11.7231 9 11.7231Z" fill="white" />
  </svg>
);

// Dois tons — o filter: brightness(0) apaga o contraste entre eles, então inverte as cores
// via prop em vez de usar o filtro.
const YoutubeIcon: FC<{ active?: boolean }> = ({ active }) => (
  <svg
    width="22"
    height="16"
    viewBox="0 0 26 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    style={{ filter: "none" }}
  >
    <path d="M25.1837 2.81868C24.8831 1.71001 24.0154 0.843959 22.9045 0.543956C20.9061 0 12.863 0 12.863 0C12.863 0 4.81982 0.0164835 2.82142 0.56044C1.71058 0.860443 0.842842 1.72649 0.542253 2.83517C-0.0622217 6.37912 -0.296745 11.7791 0.558769 15.1813C0.859358 16.29 1.7271 17.156 2.83794 17.456C4.83634 18 12.8795 18 12.8795 18C12.8795 18 20.9226 18 22.921 17.456C24.0319 17.156 24.8996 16.29 25.2002 15.1813C25.8377 11.6324 26.0343 6.23571 25.1837 2.81868Z" fill={active ? "white" : "#222343"} />
    <path d="M10.3047 12.8574L16.977 9.00021L10.3047 5.14307V12.8574Z" fill={active ? "#0C0C1D" : "white"} />
  </svg>
);

const MAX_FILE_BYTES = 150 * 1024 * 1024; // 150 MB
const ACCEPTED_TYPES = ["video/mp4", "image/jpeg", "image/png"];

const schema = yup.object().shape({
  title: yup.string().required("Título é obrigatório"),
});

interface ChatHighlightFormProps {
  show: boolean;
  highlight: any | null;
  onClose: () => void;
}

type FormValues = {
  title: string;
  description?: string;
};

const ChatHighlightForm: FC<ChatHighlightFormProps> = ({
  show,
  highlight,
  onClose,
}) => {
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [mediaMode, setMediaMode] = useState<"upload" | "loom" | "youtube">("upload");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loomUrl, setLoomUrl] = useState<string>("");
  const [youtubeUrl, setYoutubeUrl] = useState<string>("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<"video" | "image" | null>(
    null
  );
  const [fileError, setFileError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { handleCreate, handleUpdate, handleList } = useChatHighlights();

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const validateLoomUrl = (url: string): boolean => {
    if (!url.trim()) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.hostname === "www.loom.com" && (urlObj.pathname.startsWith("/share/") || urlObj.pathname.startsWith("/embed/"));
    } catch {
      return false;
    }
  };

  const convertEmbedToShareUrl = (url: string): string => {
    try {
      if (url.includes("/embed/")) {
        const embedId = url.split("/embed/")[1]?.split("?")[0];
        if (embedId) return `https://www.loom.com/share/${embedId}`;
      }
    } catch {}
    return url;
  };

  const extractYoutubeId = (url: string): string | null => {
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\.|^m\./, "");
      if (host === "youtu.be") return u.pathname.slice(1).split("/")[0] || null;
      if (host === "youtube.com") {
        if (u.pathname === "/watch") return u.searchParams.get("v");
        if (u.pathname.startsWith("/embed/")) return u.pathname.split("/embed/")[1]?.split("/")[0] || null;
        if (u.pathname.startsWith("/shorts/")) return u.pathname.split("/shorts/")[1]?.split("/")[0] || null;
      }
      return null;
    } catch {
      return null;
    }
  };

  const validateYoutubeUrl = (url: string): boolean => !!extractYoutubeId(url);

  const clearPreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setLoomUrl("");
    setYoutubeUrl("");
    setPreviewUrl(null);
    setPreviewType(null);
    setFileError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    reset({ title: "" });
    setMediaMode("upload");
    clearPreview();
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileError("");

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setFileError("Tipo de arquivo inválido. Aceitos: MP4, JPG, PNG.");
      clearPreview();
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      setFileError("Arquivo muito grande. Tamanho máximo permitido: 150 MB.");
      clearPreview();
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    const isVideo = file.type === "video/mp4";
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setPreviewType(isVideo ? "video" : "image");
  };

  const onSubmit = async (data: FormValues) => {
    if (mediaMode === "loom") {
      if (!validateLoomUrl(loomUrl)) {
        setFileError("URL do Loom inválida. Use: https://www.loom.com/share/{id}");
        return;
      }
    } else if (mediaMode === "youtube") {
      if (!validateYoutubeUrl(youtubeUrl)) {
        setFileError("URL do YouTube inválida. Use: https://www.youtube.com/watch?v={id} ou https://youtu.be/{id}");
        return;
      }
    } else {
      if (!highlight && !selectedFile) {
        setFileError("Selecione um arquivo de mídia.");
        return;
      }
    }

    setIsLoadingSubmit(true);

    const payload = {
      title: data.title,
      description: data.description ?? "",
      ...(mediaMode === "loom"
        ? { loom_url: loomUrl }
        : mediaMode === "youtube"
        ? { youtube_url: youtubeUrl }
        : { file: selectedFile }),
    };

    let ret: boolean;
    if (highlight?.id) {
      ret = await handleUpdate(highlight.id, payload);
    } else {
      ret = await handleCreate(payload);
    }

    setIsLoadingSubmit(false);

    if (ret) {
      toast.success(
        highlight?.id ? "Destaque atualizado com sucesso!" : "Destaque criado com sucesso!"
      );
      handleList();
      handleClose();
    }
  };

  useEffect(() => {
    if (show) {
      if (highlight?.id) {
        reset({ title: highlight.title, description: highlight.description ?? "" });

        if (highlight.media_type === "loom") {
          setMediaMode("loom");
          setLoomUrl(convertEmbedToShareUrl(highlight.external_url ?? ""));
          setYoutubeUrl("");
          setPreviewUrl(null);
          setPreviewType(null);
          setSelectedFile(null);
        } else if (highlight.media_type === "youtube") {
          setMediaMode("youtube");
          setYoutubeUrl(highlight.external_url ?? "");
          setLoomUrl("");
          setPreviewUrl(null);
          setPreviewType(null);
          setSelectedFile(null);
        } else {
          setMediaMode("upload");
          const baseUrl = (process.env.REACT_APP_BEVIAJUDA_API || "").replace(/\/$/, "");
          const existingUrl = highlight.filename
            ? `${baseUrl}/${highlight.filename}`
            : null;
          setPreviewUrl(existingUrl);
          setPreviewType(highlight.media_type === "video" ? "video" : "image");
          setSelectedFile(null);
          setLoomUrl("");
          setYoutubeUrl("");
        }
      } else {
        reset({ title: "" });
        setMediaMode("upload");
        clearPreview();
      }
    }
  }, [show, highlight]);

  useEffect(() => {
    return () => {
      if (previewUrl && selectedFile) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, []);

  const existingPreview = highlight?.id && !selectedFile && previewUrl;

  return (
    <ModalDefault
      title={highlight?.id ? "Editar Destaque" : "Novo Destaque"}
      show={show}
      sizeModal="lg"
      onClose={handleClose}
      handleSubmit={handleSubmit(onSubmit)}
      buttonText={highlight?.id ? "Salvar Alterações" : "Criar Destaque"}
      disabledSubmit={isLoadingSubmit}
    >
      <div className="ChatHighlightForm" data-testid="ChatHighlightForm">
        <Row>
          <Col md={12}>
            <FormGroup className="mb-3">
              <label className="form-label">
                Título <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.title ? "is-invalid" : ""}`}
                placeholder="Ex: Lançamento novo produto"
                {...register("title")}
              />
              <ErrorMessage name="Título" type={errors.title?.type} />
            </FormGroup>
          </Col>

          <Col md={12}>
            <FormGroup className="mb-3">
              <label className="form-label">Descrição</label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Ex: Uma série exclusiva desenvolvida para descomplicar os temas que mais geram dúvidas no mercado"
                {...register("description")}
              />
            </FormGroup>
          </Col>

          <Col md={12}>
            <FormGroup className="mb-3">
              <label className="form-label">
                Tipo de Mídia <span className="text-danger">*</span>
              </label>
              <div className="chat-highlight-form__media-toggle" role="group">
                <button
                  type="button"
                  className={`chat-highlight-form__media-btn ${mediaMode === "loom" ? "is-active" : ""}`}
                  onClick={() => {
                    setMediaMode("loom");
                    setSelectedFile(null);
                    setYoutubeUrl("");
                    setFileError("");
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                >
                  <LoomIcon />
                  Loom
                </button>
                <button
                  type="button"
                  className={`chat-highlight-form__media-btn ${mediaMode === "youtube" ? "is-active" : ""}`}
                  onClick={() => {
                    setMediaMode("youtube");
                    setSelectedFile(null);
                    setLoomUrl("");
                    setFileError("");
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                >
                  <YoutubeIcon active={mediaMode === "youtube"} />
                  YouTube
                </button>
                <button
                  type="button"
                  className={`chat-highlight-form__media-btn ${mediaMode === "upload" ? "is-active" : ""}`}
                  onClick={() => {
                    setMediaMode("upload");
                    setLoomUrl("");
                    setYoutubeUrl("");
                    setFileError("");
                  }}
                >
                  <CloudUploadIcon />
                  Upload local
                </button>
              </div>
            </FormGroup>
          </Col>

          <Col md={12} style={{ display: mediaMode === "loom" ? "block" : "none" }}>
            <FormGroup className="mb-3">
              <label className="form-label">
                URL do Loom <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${fileError ? "is-invalid" : ""}`}
                placeholder="Ex: https://www.loom.com/share/abc123"
                value={loomUrl}
                onChange={(e) => {
                  setLoomUrl(e.target.value);
                  setFileError("");
                }}
              />
              <small className="text-muted">
                Cole a URL completa do seu vídeo Loom
              </small>
              {fileError && (
                <div className="text-danger mt-1 small">{fileError}</div>
              )}
            </FormGroup>
          </Col>

          <Col md={12} style={{ display: mediaMode === "youtube" ? "block" : "none" }}>
            <FormGroup className="mb-3">
              <label className="form-label">
                URL do YouTube <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${fileError ? "is-invalid" : ""}`}
                placeholder="Ex: https://www.youtube.com/watch?v=abc123"
                value={youtubeUrl}
                onChange={(e) => {
                  setYoutubeUrl(e.target.value);
                  setFileError("");
                }}
              />
              <small className="text-muted">
                Cole a URL completa do seu vídeo do YouTube
              </small>
              {fileError && (
                <div className="text-danger mt-1 small">{fileError}</div>
              )}
            </FormGroup>
          </Col>

          <Col md={12} style={{ display: mediaMode === "upload" ? "block" : "none" }}>
            <FormGroup className="mb-3">
              <label className="form-label">
                Arquivo de Mídia{" "}
                {!highlight?.id && <span className="text-danger">*</span>}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                className="form-control"
                accept=".mp4,.jpg,.jpeg,.png"
                onChange={handleFileChange}
              />
              <small className="text-muted">
                Formatos aceitos: MP4, JPG, PNG — máx. 150 MB
              </small>
              {fileError && (
                <div className="text-danger mt-1 small">{fileError}</div>
              )}
            </FormGroup>
          </Col>

          {mediaMode === "loom" && loomUrl && validateLoomUrl(loomUrl) && (
            <Col md={12}>
              <div className="chat-highlight-form__preview mb-3">
                <label className="form-label">Pré-visualização (Loom)</label>
                <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden" }}>
                  <iframe
                    src={`https://www.loom.com/embed/${loomUrl.split("/share/")[1]?.split("?")[0]}?hideEmbedTopBar=true`}
                    frameBorder="0"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </div>
              </div>
            </Col>
          )}

          {mediaMode === "youtube" && youtubeUrl && validateYoutubeUrl(youtubeUrl) && (
            <Col md={12}>
              <div className="chat-highlight-form__preview mb-3">
                <label className="form-label">Pré-visualização (YouTube)</label>
                <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden" }}>
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYoutubeId(youtubeUrl)}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </div>
              </div>
            </Col>
          )}

          {(previewUrl && (selectedFile || existingPreview) && mediaMode === "upload") && (
            <Col md={12}>
              <div className="chat-highlight-form__preview mb-3">
                <label className="form-label">
                  Pré-visualização{selectedFile ? " (novo arquivo)" : " (atual)"}
                </label>

                {previewType === "video" ? (
                  <video
                    key={previewUrl}
                    controls
                    className="chat-highlight-form__video"
                  >
                    <source src={previewUrl} />
                    Seu navegador não suporta vídeo.
                  </video>
                ) : (
                  <img
                    src={previewUrl}
                    alt="Pré-visualização"
                    className="chat-highlight-form__image"
                  />
                )}

                {selectedFile && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary mt-2"
                    onClick={clearPreview}
                  >
                    Remover arquivo selecionado
                  </button>
                )}
              </div>
            </Col>
          )}
        </Row>
      </div>
    </ModalDefault>
  );
};

export default ChatHighlightForm;
